import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listStockLedger } from '../../api/inventory';
import { listItems } from '../../api/masterData';
import { listWarehouses } from '../../api/orgData';
import { useBranch } from '../../context/BranchContext';
import {
  EmptyState,
  ErrorNote,
  ListToolbar,
  LoadingNote,
  Pagination,
  PageHeader,
  Select,
  SummaryStrip,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
} from '../../components/common';
import type { Item, StockLedgerEntry, StockMovementType, Warehouse } from '../../types/domain';

/** Every movement type this ledger can contain maps to the real module
 * page that owns its source document — clicking a reference navigates
 * there for real instead of showing inert text. ISSUE has no dedicated
 * page yet in this vertical slice, so it stays plain text rather than
 * linking to a destination that doesn't exist. */
const REF_MODULE: Partial<Record<StockMovementType, { path: string; label: string }>> = {
  GR: { path: '/app/goods-receipts', label: 'Lihat di Goods Receipts' },
  ADJUSTMENT_IN: { path: '/app/stock-opname', label: 'Lihat di Stock Opname' },
  ADJUSTMENT_OUT: { path: '/app/stock-opname', label: 'Lihat di Stock Opname' },
  TRANSFER_IN: { path: '/app/transfers', label: 'Lihat di Transfers' },
  TRANSFER_OUT: { path: '/app/transfers', label: 'Lihat di Transfers' },
  WASTE: { path: '/app/waste', label: 'Lihat di Waste' },
};

const MOVEMENT_TYPES: StockMovementType[] = ['GR', 'ISSUE', 'ADJUSTMENT_IN', 'ADJUSTMENT_OUT', 'TRANSFER_IN', 'TRANSFER_OUT', 'WASTE'];

/**
 * Stock Ledger — read-only view of the immutable append-only ledger
 * (migration 0008). No write path exists here or anywhere in the frontend;
 * the only way a row appears is via posting a GR/Stock Opname/Transfer/
 * Waste. Supports ?item=<id> and ?warehouse=<id> (either alone or
 * together) to arrive pre-filtered from Stock Balance — a real
 * cross-page drilldown, not a dead link — and each row's reference
 * navigates to the module that owns the source document.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function StockLedgerPage() {
  const { activeBranchId } = useBranch();
  const [searchParams, setSearchParams] = useSearchParams();
  const itemFilter = searchParams.get('item');
  const warehouseFilter = searchParams.get('warehouse');
  const [entries, setEntries] = useState<StockLedgerEntry[]>([]);
  const [itemsById, setItemsById] = useState<Record<string, Item>>({});
  const [warehousesById, setWarehousesById] = useState<Record<string, Warehouse>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  // stock_ledger.branch_id is a real FK — scoped to the active branch,
  // re-fetching whenever it changes. The ?item=/?warehouse= URL params
  // narrow further on the client (itemScoped below).
  useEffect(() => {
    setLoading(true);
    Promise.all([listStockLedger(undefined, activeBranchId ?? undefined), listItems(), listWarehouses()])
      .then(([ledgerRows, itemRows, warehouseRows]) => {
        setEntries(ledgerRows);
        setItemsById(Object.fromEntries(itemRows.map((it) => [it.item_id, it])));
        setWarehousesById(Object.fromEntries(warehouseRows.map((w) => [w.warehouse_id, w])));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const itemScoped = entries.filter((e) => (!itemFilter || e.item_id === itemFilter) && (!warehouseFilter || e.warehouse_id === warehouseFilter));
  const filteredItemLabel = itemFilter ? (itemsById[itemFilter]?.item_code ?? itemFilter) : null;
  const filteredWarehouseLabel = warehouseFilter ? (warehousesById[warehouseFilter]?.name ?? warehouseFilter) : null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return itemScoped.filter((e) => {
      if (typeFilter !== 'ALL' && e.movement_type !== typeFilter) return false;
      if (q) {
        const code = itemsById[e.item_id]?.item_code?.toLowerCase() ?? '';
        const name = itemsById[e.item_id]?.name?.toLowerCase() ?? '';
        if (!code.includes(q) && !name.includes(q) && !e.ref_id.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [itemScoped, typeFilter, search, itemsById]);

  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 20);
  const inflow = filtered.filter((e) => e.qty_delta > 0).length;
  const outflow = filtered.filter((e) => e.qty_delta < 0).length;

  return (
    <div>
      <PageHeader title="Stock Ledger" subtitle="Riwayat pergerakan stok, immutable (append-only) — migration 0008. 200 baris terbaru." />
      {(itemFilter || warehouseFilter) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 'var(--alex-fs-caption)', flexWrap: 'wrap' }}>
          {itemFilter && (
            <span style={{ background: 'var(--alex-primary-soft)', color: 'var(--alex-primary)', padding: '4px 10px', borderRadius: 999, fontWeight: 700 }}>
              Difilter: item {filteredItemLabel}
            </span>
          )}
          {warehouseFilter && (
            <span style={{ background: 'var(--alex-primary-soft)', color: 'var(--alex-primary)', padding: '4px 10px', borderRadius: 999, fontWeight: 700 }}>
              Difilter: gudang {filteredWarehouseLabel}
            </span>
          )}
          <button
            onClick={() => setSearchParams({})}
            style={{ background: 'none', border: 'none', color: 'var(--alex-text-muted)', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit', padding: 0 }}
          >
            Hapus filter
          </button>
        </div>
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Baris', value: itemScoped.length },
              { label: 'Masuk (+)', value: inflow, tone: 'success' },
              { label: 'Keluar (-)', value: outflow, tone: 'danger' },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari item atau referensi..."
            resultCount={total}
            filters={
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 180 }}>
                <option value="ALL">Semua tipe</option>
                {MOVEMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            }
          />
          {itemScoped.length === 0 && (
            <EmptyState text={itemFilter || warehouseFilter ? 'Tidak ada pergerakan stok yang cocok dengan filter ini.' : 'Belum ada pergerakan stok.'} />
          )}
          {itemScoped.length > 0 && total === 0 && <EmptyState text="Tidak ada baris yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Waktu</th>
                  <th style={thStyle}>Item</th>
                  <th style={thStyle}>Gudang</th>
                  <th style={thStyle}>Tipe</th>
                  <th style={thStyle}>Qty</th>
                  <th style={thStyle}>Referensi</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((e) => {
                  const mod = REF_MODULE[e.movement_type];
                  return (
                    <tr key={e.ledger_id}>
                      <td style={tdStyle}>{formatDateTime(e.occurred_at)}</td>
                      <td style={tdStyle}>{itemsById[e.item_id]?.item_code ?? e.item_id}</td>
                      <td style={tdStyle}>{warehousesById[e.warehouse_id]?.name ?? e.warehouse_id}</td>
                      <td style={tdStyle}>{e.movement_type}</td>
                      <td style={{ ...tdStyle, color: e.qty_delta >= 0 ? 'var(--alex-success)' : 'var(--alex-danger)' }}>
                        {e.qty_delta >= 0 ? '+' : ''}
                        {e.qty_delta}
                      </td>
                      <td style={tdStyle}>
                        {mod ? (
                          <Link to={mod.path} style={{ color: 'var(--alex-primary)', fontWeight: 600, textDecoration: 'none' }} title={`${e.ref_type} ${e.ref_id}`}>
                            {mod.label} →
                          </Link>
                        ) : (
                          <span title={e.ref_id}>
                            {e.ref_type} {e.ref_id.slice(0, 8)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
    </div>
  );
}
